import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useDispatch } from '../hooks';
import { connect } from 'react-redux';
import { InternalServerError, HypotheekbondLogo } from '../components';
import { InputPageContainer, Footer } from '../containers';
import { NavigationButtons } from '../buttons';
import { NAVIGATE } from './actions';
import { shapeSettings } from '../../tools/maxmortgage/config';
import { Disclaimer, Navigation } from './';

export const Wrapper = styled.div`
  position: relative;
  width: 100%;
  min-height: 500px;
  background-color: ${({ theme }) => theme.backgroundColor};
  box-sizing: border-box;

  @media ${({ theme }) => theme.mobile} {
    padding-left: 2px;
    padding-right: 2px;
    background-color: ${({ theme }) => theme.cardColor};
  }
`;

export const Main = styled.main`
  display: flex;
  justify-content: center;
  align-items: stretch;
  flex-wrap: wrap;

  @media ${({ theme }) => theme.mobile} {
    flex-direction: ${({isReversed}) => isReversed ? 'column' : 'column-reverse'};
    align-items: center;
  }
`;

export const InputContainer = styled(InputPageContainer)`
  box-sizing: border-box;
  width: 40%;
  flex-grow: 1;
  flex-shrink: 0;
  max-width: 466px;
  margin: 0;
  
  @media ${({ theme }) => theme.mobile} {
    width: 100%;
  }
`;

export const ResultContainer = styled.div`
  box-sizing: border-box;
  width: 60%;
  flex-grow: 1;
  flex-shrink: 0;
  padding-left: 40px;

  &:empty {
    display: none;
  }

  @media ${({ theme }) => theme.mobile} {
    width: 100%;
    max-width: 466px;
    padding: 0;
    margin: 0 0 40px;
    width: 100%;
  }
`;

const ToolContainer = ({
  Input,
  Output,
  pages,
  syncing,
  recalculating,
  input,
  settings,
  broken,
  customDisclaimer,
  override,
  pagesInWizard,
  hasNavigation,
  isReversed
}) => {
  // seems state is broken in Safari inside an iframe
  // https://github.com/remix-run/react-router/discussions/10371
  const { state } = useLocation();
  const dispatch = useDispatch();
  const currentPageIndex = pages.indexOf(state?.page ?? pages[0]);
  const steps = pagesInWizard ? pagesInWizard : pages;
  const isLastPage = currentPageIndex === pages.length - 1;
  const isLastStep = currentPageIndex === steps.length - 1;

  let nextStep = input.next_step_text || 'Volgende stap';
  if (recalculating) nextStep = 'Herberekenen';
  else if (currentPageIndex === steps.length - 2) nextStep = 'Berekenen';

  const previousHandler = e => {
    e.preventDefault();
    dispatch({ type: NAVIGATE, delta: -1 });
  };
  const nextHandler = e => {
    e.preventDefault();
    dispatch({
      type: NAVIGATE,
      delta: recalculating ? recalculating - currentPageIndex : 1,
    });
  };

  const pageContent = override ? override : (
    <>
      <InputContainer as="form">
        {Input}
        <NavigationButtons
          showNextButton={!input.hide_next_step_button && !isLastStep && !isLastPage}
          onClickNext={nextHandler}
          nextLabel={nextStep}
          showPreviousButton={!recalculating && currentPageIndex > 0}
          onClickPrevious={previousHandler}
          previousLabel={isLastStep ? 'Gegevens aanpassen' : 'Vorige stap'}
          loading={syncing}
        />
      </InputContainer>
      <ResultContainer>{Output}</ResultContainer>
    </>
  );

  return (
    <Wrapper>
      {hasNavigation ? (
        <Navigation />
      ) : null}
      <Main isReversed={isReversed}>
        {broken ? (
          <InputContainer>
            <InternalServerError />
          </InputContainer>
        ) : pageContent}
      </Main>
      <Footer>
        {settings.enableDisclaimer && (
          <Disclaimer settings={settings} toolSpecificDisclaimer={customDisclaimer} />
        )}
        {settings && settings.enableHypotheekbondLogo && <HypotheekbondLogo />}
      </Footer>
    </Wrapper>
  );
};

ToolContainer.propTypes = {
  app: PropTypes.string.isRequired,
  pages: PropTypes.instanceOf(Array).isRequired,
  Input: PropTypes.node.isRequired,
  Output: PropTypes.node.isRequired,
  sync: PropTypes.func,
  syncing: PropTypes.bool.isRequired,
  broken: PropTypes.bool.isRequired,
  settings: shapeSettings,
  input: PropTypes.shape(),
  forceStep: PropTypes.number,
  customDisclaimer: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  override: PropTypes.element,
  pagesInWizard: PropTypes.arrayOf(PropTypes.string),
  hasNavigation: PropTypes.bool,
};

ToolContainer.defaultProps = {
  recalculating: 0,
  input: {},
  sync: () => {},
  forceStep: null,
  settings: null,
  customDisclaimer: '',
  override: null,
  pagesInWizard: null,
  hasNavigation: true,
  isReversed: false,
};

export default connect(
  state => ({
    recalculating: state.recalculating,
    settings: state.settings,
    syncing: state.syncing,
    broken: state.broken,
    pages: state.pages,
    pagesInWizard: state.pagesInWizard,
    app: state.app,
    input: state.input,
  })
)(ToolContainer);
